import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { ForecastPeriod } from 'Types/ForecastFrequency';
// eslint-disable-next-line
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { SensorUpsellBanner } from 'components/elements/Banners/SensorUpsellBanner';
import { SeafoamCheckbox } from 'components/elements/Checkbox/SeafoamCheckbox';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import ToggleSwitch from 'components/elements/Toggles/ToggleSwitch';
import OccupancyGraph from 'components/structure/Analytics/OccupancyGraph';
import PredictionGraph from 'components/structure/Analytics/PredictionsGraph';
import TimeSeriesBarGraph from 'components/structure/Analytics/TimeSeriesBarGraph';
import {
  ApplessOccupancyLogsData,
  ApplessOccupancyLogsTable
} from 'components/structure/Space/SpaceAnalytics/ApplessOccupancyLogsTable';
import { PeakData, PeakSpaceTable } from 'components/structure/Space/SpaceAnalytics/PeakSpaceTable';
import {
  PopularityData,
  SpacePopularityTable
} from 'components/structure/Space/SpaceAnalytics/SpacePopularityTable';
import ExportSpaceCSV from 'components/structure/Space/SpaceForms/exportSpaceCSV';
import { formatISO, subHours } from 'date-fns';
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import SpaceService, {
  AggregatedUsageResponse,
  Aggregation,
  ApplessOccupancyEventResponse,
  ApplessOccupancySensorResponse,
  BuildingAggregatedUsageResponse,
  ForecastResponseProps,
  OccupancyResponse,
  SpaceProps
} from 'services/hooks/space-service';
import {
  ApplessOccupancyEventType,
  transformApplessOccupancyLogsData
} from 'utils/applessOccupancyUtils';
import { colours, SpaceReportingType } from 'utils/constants';
import { containerStyle } from 'utils/styles';
import { formatDateToString } from 'utils/time';

const LS_KEY_BUILDING = 'bl.spaces.reporting.building';
const LS_KEY_SPACE = 'bl.spaces.reporting.space';
const LS_KEY_SPACE_REPORTING_TAB = 'bl.spaces.reporting.tab';
const ALL_SPACES = 'All Spaces';

// The occupants in a space at the same time. Data comes in 10 minute increments.
function getOccupancyGraphData(
  rawData?: OccupancyResponse,
  buildingTimezone = 'UTC'
): {
  dates: Date[];
  occupants: number[];
} {
  const dates: Date[] = [];
  const occupants: number[] = [];

  if (!!rawData) {
    // Convert rawData keys to dates in the specified timezone
    const rawDates = Object.keys(rawData).map((dateStr) =>
      utcToZonedTime(zonedTimeToUtc(dateStr, buildingTimezone), buildingTimezone)
    );

    // Ensure rawDates are sorted
    if (rawDates && rawDates.length) {
      try {
        rawDates.sort((a, b) => a.getTime() - b.getTime());
      } catch (e) {
        console.error('Problem sorting times: ', e);
      }
    }

    // Determine the start and end dates
    const startDate = rawDates[0];
    const endDate = rawDates[rawDates.length - 1];

    if (startDate && endDate) {
      try {
        // Iterate through the date range in 10-minute increments
        for (let time = startDate.getTime(); time <= endDate.getTime(); time += 10 * 60 * 1000) {
          const date = new Date(time);
          const dateStr = format(date, 'yyyy-MM-dd HH:mm:ss', { timeZone: buildingTimezone });

          dates.push(date);
          occupants.push(rawData[dateStr] || 0);
        }
      } catch (e) {
        console.error('Problem counting occupants: ', e);
      }
    }
  }

  return { dates, occupants };
}

function splitDataIntoPastAndFuture(
  dates: Date[],
  values: number[],
  today: Date
): { past: { dates: Date[]; values: number[] }; future: { dates: Date[]; values: number[] } } {
  const past: { dates: Date[]; values: number[] } = { dates: [], values: [] };
  const future: { dates: Date[]; values: number[] } = { dates: [], values: [] };

  dates.forEach((date, index) => {
    if (date < today) {
      past.dates.push(date);
      past.values.push(values[index]);
    } else {
      future.dates.push(date);
      future.values.push(values[index]);
    }
  });

  return { past, future };
}

function getForecastGraphData(
  startDate: Date,
  endDate: Date,
  frequency: ForecastPeriod,
  forecastData?: ForecastResponseProps[],
  buildingTimezone = 'UTC'
) {
  const dates: Date[] = [];
  const forecastValues: number[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (forecastData) {
    try {
      if (frequency === ForecastPeriod.Yearly) {
        // Map to store the max value for each date
        const dailyMaxValues: { [date: string]: number } = {};

        forecastData.forEach((dataPoint) => {
          const [dateStr, value] = Object.entries(dataPoint)[0];
          const zonedDate = utcToZonedTime(
            zonedTimeToUtc(dateStr, buildingTimezone),
            buildingTimezone
          );

          const dateKey = zonedDate.toISOString().split('T')[0];
          const forecastValue = Number(value);

          // Only update if the new value is greater
          if (!dailyMaxValues[dateKey] || dailyMaxValues[dateKey] < forecastValue) {
            dailyMaxValues[dateKey] = forecastValue;
          }
        });

        // Populate the dates and forecastValues arrays with the max values
        for (const [dateKey, maxValue] of Object.entries(dailyMaxValues)) {
          dates.push(new Date(dateKey));
          forecastValues.push(maxValue);
        }
      } else {
        // For other frequencies, use the original data points
        forecastData.forEach((dataPoint) => {
          const [dateStr, value] = Object.entries(dataPoint)[0];
          const zonedDate = utcToZonedTime(
            zonedTimeToUtc(dateStr, buildingTimezone),
            buildingTimezone
          );

          // Set rangeStart and rangeEnd based on the frequency
          let rangeStart: Date;
          let rangeEnd: Date;

          switch (frequency) {
            case ForecastPeriod.Daily:
              rangeStart = new Date();
              rangeEnd = new Date();
              rangeStart.setDate(rangeStart.getDate() - 1);
              rangeEnd.setDate(rangeEnd.getDate() + 1);
              break;
            case ForecastPeriod.Weekly:
              rangeStart = new Date();
              rangeEnd = new Date();
              rangeStart.setDate(rangeStart.getDate() - 7);
              rangeEnd.setDate(rangeEnd.getDate() + 7);
              break;
            case ForecastPeriod.Monthly:
              rangeStart = new Date();
              rangeEnd = new Date();
              rangeStart.setDate(rangeStart.getDate() - 30);
              rangeEnd.setDate(rangeEnd.getDate() + 30);
              break;
            case ForecastPeriod.Custom:
              rangeStart = new Date(startDate);
              rangeEnd = new Date(endDate);
              break;
            default:
              rangeStart = new Date(startDate);
              rangeEnd = new Date(endDate);
              break;
          }

          if (zonedDate >= rangeStart && zonedDate <= rangeEnd) {
            dates.push(zonedDate);
            forecastValues.push(Number(value));
          }
        });
      }
    } catch (e) {
      console.error('Problem processing forecast data: ', e);
    }
  }

  return splitDataIntoPastAndFuture(dates, forecastValues, today);
}

function getPeakUsageForAllSpaces(
  spaces: SpaceProps[] | undefined,
  aggregatedUtilization?: AggregatedUsageResponse
): PeakData[] {
  if (!spaces || !aggregatedUtilization) {
    return [];
  }

  return spaces
    .flatMap((space) => {
      if (!space.uuid) return []; // Skip processing if space UUID is undefined

      const utilizationData = aggregatedUtilization.find((item) =>
        item.hasOwnProperty(space.uuid ?? '')
      );
      if (!utilizationData) return []; // Skip if no usage data is found for the space

      const data = utilizationData[space.uuid];
      const peakIndex = data.y.reduce((iMax, x, i, arr) => (x > arr[iMax] ? i : iMax), 0);
      const capacity = Number(space.capacity); // Ensure capacity is treated as a number

      // Construct the PeakData object, including capacity
      return [
        {
          name: space.name,
          occupancy: data.y[peakIndex],
          time: data.x[peakIndex],
          capacity: isNaN(capacity) ? 0 : capacity // Provide a fallback value if capacity is not a valid number
        }
      ];
    })
    .filter((item) => item !== null); // This is technically unnecessary due to the `return [];` in our map function but kept for clarity
}

function getSpaceCapacity(
  allSpacesData: SpaceProps[] | undefined,
  selectedSpaceUuid: string,
  selectedBuildingUuid: string,
  selectedBuildingCapacity: number | undefined
): number {
  let selectedSpaceCapacity = 0;
  if (!!selectedSpaceUuid) {
    if (selectedSpaceUuid === ALL_SPACES) {
      // the building has a capacity, use it
      if (!!selectedBuildingCapacity) {
        selectedSpaceCapacity = selectedBuildingCapacity;
      } else if (!!allSpacesData) {
        // otherwise, sum up the capacity of all spaces
        selectedSpaceCapacity = allSpacesData.reduce((sum, space) => {
          if (space.buildingUuid === selectedBuildingUuid) {
            const capacity = Number(space.capacity);
            return sum + (isNaN(capacity) ? 0 : capacity);
          }
          return sum;
        }, 0);
      }
    } else if (!!allSpacesData) {
      selectedSpaceCapacity = Number(
        allSpacesData.find((space) => space.uuid === selectedSpaceUuid)?.capacity ?? 0
      );
    }
  }

  return selectedSpaceCapacity;
}

function getAverageOccupancyForSpace(
  spaceUuid: string,
  aggregatedUtilization?: AggregatedUsageResponse
): number {
  if (!aggregatedUtilization) {
    return 0;
  }

  // Attempt to find the utilization data for the specified space UUID
  const targetData = aggregatedUtilization.find((item) => item.hasOwnProperty(spaceUuid));

  if (!targetData) {
    return 0; // Returning 0 or an indication that no data was found for the given UUID
  }

  // Extract the y array for the given space UUID
  const yArray = targetData[spaceUuid].y;

  // Calculate the average occupancy if yArray is not empty
  if (yArray.length > 0) {
    const sum = yArray.reduce((acc, curr) => acc + curr, 0);
    const average = sum / yArray.length;
    return average;
  } else {
    return 0; // Returning 0 as there's no occupancy data to calculate an average
  }
}

function generatePopularityRows(
  spaces?: SpaceProps[],
  aggregatedUtilization?: AggregatedUsageResponse
): PopularityData[] {
  const rows: PopularityData[] = [];

  if (!!spaces) {
    spaces.forEach((space) => {
      if (space.uuid) {
        rows.push({
          name: space.name,
          avgOccupancy: getAverageOccupancyForSpace(space.uuid, aggregatedUtilization),
          capacity: Number(space.capacity)
        });
      }
    });
  }

  return rows;
}

function generateUtilizationRows(
  spaces: SpaceProps[],
  usage?: AggregatedUsageResponse
): AggregatedUsageResponse {
  if (!spaces || !usage) {
    return [];
  }

  // Filter out spaces without a UUID
  const validSpaces = spaces.filter((space) => !!space.uuid);

  // Initialize an empty array for the final response
  const utilization: AggregatedUsageResponse = [];

  validSpaces.forEach((space) => {
    // Assuming space.uuid is defined based on the above filter
    const usageItem = usage.find((item) => item.hasOwnProperty(space.uuid ?? ''));

    if (usageItem && !!space.uuid) {
      // Retrieve the existing usage data for the current space
      const data = usageItem[space.uuid];

      // Populate the y values
      const uniqueOccupants: number[] = data.y.map((val) => val);

      // Construct and push the new item to the utilization array
      utilization.push({
        [space.uuid]: {
          x: data.x,
          y: uniqueOccupants
        }
      });
    }
  });

  return utilization;
}

function getSelectedSpaceGraphData(
  aggregatedUsageResponse: AggregatedUsageResponse | undefined,
  selectedSpaceUuid: string
): { x: string[]; y: number[] } | null {
  if (!aggregatedUsageResponse) {
    return null;
  }

  const targetData = aggregatedUsageResponse.find((item) => item.hasOwnProperty(selectedSpaceUuid));

  // If found, return the x and y arrays; otherwise, return null
  if (targetData) {
    return targetData[selectedSpaceUuid];
  } else {
    return null;
  }
}

/**
 * Calculates the appless occupancy over time based on occupancy event logs.
 * This function processes a series of occupancy events and generates a timeline
 * of occupancy levels between the specified start and end dates.
 *
 * @param logs - Array of ApplessOccupancyEventResponse objects containing occupancy events
 * @param startDate - The start date for the occupancy calculation
 * @param endDate - The end date for the occupancy calculation
 * @returns An object with two arrays: 'dates' containing timestamps and 'occupants' containing occupancy levels
 */
const calculateApplessOccupancyOverTime = (
  logs: ApplessOccupancyEventResponse[] | undefined,
  startDate: Date,
  endDate: Date
) => {
  if (!logs?.length) {
    return { dates: [], occupants: [] };
  }

  const occupancyData: { dates: Date[]; occupants: number[] } = { dates: [], occupants: [] };

  // Sort all logs, including those before the start date
  const allSortedLogs = logs.sort(
    (a, b) => new Date(a.sensor_timestamp).getTime() - new Date(b.sensor_timestamp).getTime()
  );

  // Calculate initial occupancy
  let currentOccupancy = 0;
  for (const log of allSortedLogs) {
    const logTimestamp = new Date(log.sensor_timestamp);
    if (logTimestamp >= startDate) break;

    switch (log.event) {
      case ApplessOccupancyEventType.Entry:
        currentOccupancy++;
        break;
      case ApplessOccupancyEventType.Exit:
        currentOccupancy = Math.max(0, currentOccupancy - 1);
        break;
      case ApplessOccupancyEventType.Set:
      case ApplessOccupancyEventType.Reset:
        currentOccupancy = log.event_value;
        break;
    }
  }

  // Filter logs within the date range
  const sortedLogs = allSortedLogs.filter((log) => {
    const logTimestamp = new Date(log.sensor_timestamp);
    return logTimestamp >= startDate && logTimestamp <= endDate;
  });

  if (sortedLogs.length === 0) {
    occupancyData.dates.push(new Date(startDate), new Date(endDate));
    occupancyData.occupants.push(currentOccupancy, currentOccupancy);
    return occupancyData;
  }

  let lastEventTime = new Date(startDate);

  // Add initial occupancy at start date
  occupancyData.dates.push(new Date(startDate));
  occupancyData.occupants.push(currentOccupancy);

  for (const log of sortedLogs) {
    const logTimestamp = new Date(log.sensor_timestamp);

    if (logTimestamp > lastEventTime) {
      occupancyData.dates.push(new Date(lastEventTime));
      occupancyData.occupants.push(currentOccupancy);
    }

    switch (log.event) {
      case ApplessOccupancyEventType.Entry:
        currentOccupancy++;
        break;
      case ApplessOccupancyEventType.Exit:
        currentOccupancy = Math.max(0, currentOccupancy - 1);
        break;
      case ApplessOccupancyEventType.Set:
      case ApplessOccupancyEventType.Reset:
        currentOccupancy = log.event_value;
        break;
    }

    occupancyData.dates.push(new Date(logTimestamp));
    occupancyData.occupants.push(currentOccupancy);

    lastEventTime = logTimestamp;
  }

  return occupancyData;
};

const countApplessEvents = (logs: ApplessOccupancyLogsData[]) => {
  // Filter logs to count entries and exits for today in the building's timezone
  const entries = logs.filter((log) => {
    return log.event === ApplessOccupancyEventType.Entry;
  }).length;

  const exits = logs.filter((log) => {
    return log.event === ApplessOccupancyEventType.Exit;
  }).length;

  return { entries, exits };
};

const aggregateApplessOccupancyData = (
  dates: Date[],
  occupants: number[],
  aggregation: 'hourly' | 'daily' | 'monthly' | 'yearly',
  timeZone: string
): { x: string[]; y: number[] } => {
  // If there are no dates or occupants, return empty arrays
  if (dates.length === 0 || occupants.length === 0) {
    return { x: [], y: [] };
  }

  const aggregationMap: { [key: string]: { total: number; count: number } } = {};

  dates.forEach((date, index) => {
    const occupancy = occupants[index];
    // Convert the date to the specified time zone
    const zonedDate = utcToZonedTime(date, timeZone);
    let key = '';

    switch (aggregation) {
      case 'hourly':
        key = format(zonedDate, 'H', { timeZone }); // Hour of the day (0-23)
        break;
      case 'daily':
        key = format(zonedDate, 'E', { timeZone }); // Day of the week (1-7, where 1 is Monday)
        break;
      case 'monthly':
        key = format(zonedDate, 'M', { timeZone }); // Month of the year (1-12)
        break;
      case 'yearly':
        key = format(zonedDate, 'yyyy', { timeZone }); // Year (e.g., 2023)
        break;
      default:
        break;
    }

    if (!aggregationMap[key]) {
      aggregationMap[key] = { total: 0, count: 0 };
    }

    aggregationMap[key].total += occupancy;
    aggregationMap[key].count += 1;
  });

  const x: string[] = [];
  const y: number[] = [];

  for (const [key, value] of Object.entries(aggregationMap)) {
    x.push(key);
    y.push(value.total / (value.count || 1)); // Calculate the average
  }

  // Sort the x-axis
  if (aggregation === 'daily') {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    x.sort((a, b) => days.indexOf(a) - days.indexOf(b));
  } else {
    x.sort((a, b) => parseInt(a) - parseInt(b)); // Other aggregations can use their parseInt value
  }

  const formattedX = x.map((key) => {
    switch (aggregation) {
      case 'hourly':
      case 'daily': {
        return key;
      }
      case 'monthly': {
        // Parse the month index (ensuring it's between 0 and 11 for January to December)
        const monthIndex = (parseInt(key, 10) - 1) % 12;
        return format(new Date(2024, monthIndex), 'MMMM', { timeZone });
      }
      case 'yearly':
        return key;
      default:
        return key;
    }
  });

  return { x: formattedX, y };
};

export default function SpaceReporting() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // get values from localstorage
  const lsSelectedBuildingUuid = localStorage.getItem(LS_KEY_BUILDING);
  const lsSelectedSpaceUuid = localStorage.getItem(LS_KEY_SPACE);
  const lsSelectedTab = localStorage.getItem(LS_KEY_SPACE_REPORTING_TAB);
  const lsSelectedTabSensor =
    lsSelectedTab === SpaceReportingType.People
      ? SpaceReportingType.People
      : SpaceReportingType.Occupants;

  const currentDate = new Date();
  const dateLastMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const [startDate, setStartDate] = useState(dateLastMonth);
  const [endDate, setEndDate] = useState(endOfToday);
  const [spacesInBuilding, setSpacesInBuilding] = useState<SpaceProps[]>([]);
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');
  const [selectedBuildingTimezone, setSelectedBuildingTimezone] = useState<string>('UTC');
  const [selectedBuildingCapacity, setSelectedBuildingCapacity] = useState<number | undefined>(
    undefined
  );
  const [selectedSpaceUuid, setSelectedSpaceUuid] = useState<string>('');
  const [selectedAggregation, setSelectedAggregation] = useState<Aggregation>('hourly');
  const [showMaxCapacity, setShowMaxCapacity] = React.useState(false);
  const [showMaxCapacityPrediction, setShowMaxCapacityPrediction] = React.useState(false);
  const [showMaxCapacityAppless, setShowMaxCapacityAppless] = React.useState(false);
  const [selectedForecastPeriod, setSelectedForecastPeriod] = useState<ForecastPeriod>(
    ForecastPeriod.Monthly
  );
  const [selectedSpaceReportingType, setSpaceReportingType] =
    useState<SpaceReportingType>(lsSelectedTabSensor);

  // Buildings query
  const { isLoading: isBuildingsLoading, data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Problem fetching buildings.');
      }
    }
  );

  // Spaces query
  const { isLoading: isAllSpacesLoading, data: allSpacesData } = useQuery<SpaceProps[], Error>(
    'allSpaces',
    SpaceService.findAllSpaces,
    {
      onError: () => {
        toast.error('Problem fetching spaces.');
      }
    }
  );

  // Aggregated Space Usage query
  const { isLoading: isSpaceUsageLoading, data: spaceUsageData } = useQuery<
    AggregatedUsageResponse,
    Error
  >(
    ['spaceUsage', selectedBuildingUuid, selectedAggregation, startDate, endDate],
    () => {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);

      return SpaceService.spaceAggregatedUniqueOccupants({
        buildingUuid: selectedBuildingUuid,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(nextDay),
        aggregation: selectedAggregation
      });
    },
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate && !!selectedAggregation,
      onError: () => {
        toast.error('Problem fetching space aggregated usage.');
      }
    }
  );

  // Aggregated Building Usage query
  const { isLoading: isBuildingUsageLoading, data: buildingUsageData } = useQuery<
    BuildingAggregatedUsageResponse,
    Error
  >(
    ['buldingUsage', selectedBuildingUuid, selectedAggregation, startDate, endDate],
    () => {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);

      return SpaceService.buildingAggregatedUniqueOccupants({
        buildingUuid: selectedBuildingUuid,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(nextDay),
        aggregation: selectedAggregation
      });
    },
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate && !!selectedAggregation,
      onError: () => {
        toast.error('Problem fetching building aggregated usage.');
      }
    }
  );

  // Space (or All Spaces) Occupancy query
  const { isLoading: isSpaceOccupancyLoading, data: spaceOccupancyData } = useQuery<
    OccupancyResponse,
    Error
  >(
    ['buldingOccupancy', selectedSpaceUuid, selectedBuildingUuid, startDate, endDate],
    () => {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);

      return SpaceService.spaceOccupancy({
        buildingUuid: selectedBuildingUuid,
        spaceUuid: selectedSpaceUuid,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(nextDay)
      });
    },
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate && !!selectedAggregation,
      onError: () => {
        toast.error('Problem fetching building occupancy.');
      }
    }
  );

  // Query for prediction
  function adjustDateRange(frequency: ForecastPeriod, start: Date, end: Date) {
    let adjustedStartDate = new Date(start);
    let adjustedEndDate = new Date(end);

    switch (frequency) {
      case ForecastPeriod.Daily:
      case ForecastPeriod.Weekly:
      case ForecastPeriod.Monthly:
        adjustedStartDate = new Date();
        adjustedEndDate = new Date();
        adjustedStartDate.setMonth(adjustedStartDate.getMonth() - 1);
        adjustedEndDate.setMonth(adjustedEndDate.getMonth() + 1);
        break;
      case ForecastPeriod.Yearly:
        adjustedStartDate.setFullYear(adjustedStartDate.getFullYear() - 1);
        adjustedEndDate.setFullYear(adjustedEndDate.getFullYear() + 1);
        break;
      case ForecastPeriod.Custom:
      default:
        break;
    }
    return { adjustedStartDate, adjustedEndDate };
  }

  // Fetch forecast data and split into past and future
  const adjustedSpaceUuid = selectedSpaceUuid === 'All Spaces' ? '' : selectedSpaceUuid;

  const { isLoading: isPredictionLoading, data: predictionData } = useQuery<
    ForecastResponseProps[],
    Error
  >(
    [
      'forecast',
      adjustedSpaceUuid,
      selectedBuildingUuid,
      startDate,
      endDate,
      selectedForecastPeriod
    ],
    () => {
      const { adjustedStartDate, adjustedEndDate } = adjustDateRange(
        selectedForecastPeriod,
        startDate,
        endDate
      );
      return SpaceService.getForecast({
        buildingUuid: selectedBuildingUuid,
        spaceUuid: adjustedSpaceUuid,
        startDate: formatDateToString(adjustedStartDate),
        endDate: formatDateToString(adjustedEndDate)
      });
    },
    { enabled: !!selectedBuildingUuid && !!selectedSpaceUuid && !!startDate && !!endDate }
  );

  // Appless Occupancy Sensors query
  const { isLoading: isApplessOccupancySensorsLoading, data: applessOccupancySensorsData } =
    useQuery<ApplessOccupancySensorResponse[], Error>(
      ['applessOccupancySensors', selectedBuildingUuid],
      () => SpaceService.applessOccupancySensors(),
      {
        enabled: !!selectedBuildingUuid,
        onError: () => {
          toast.error('Problem fetching appless occupancy sensors.');
        }
      }
    );

  // Appless Occupancy Lidar Logs query
  const { isLoading: isApplessOccupancyEventsLoading, data: applessOccupancyEventsData } = useQuery<
    ApplessOccupancyEventResponse[],
    Error
  >(
    ['applessOccupancyEvents', selectedBuildingUuid, startDate, endDate],
    () =>
      SpaceService.applessOccupancyEvents({
        buildingUuid: selectedBuildingUuid,
        excludePing: true,
        // fetch data from 24h before startDate to understand the starting occupancy
        startDate: formatISO(subHours(startDate, 24)),
        endDate: formatISO(endDate)
      }),
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate,
      onError: () => {
        toast.error('Problem fetching appless occupancy events.');
      }
    }
  );

  const handleFrequencyChange = (event: SelectChangeEvent) => {
    setSelectedForecastPeriod(event.target.value as ForecastPeriod);
  };

  useEffect(() => {
    if (!!selectedBuildingUuid && !!allSpacesData) {
      // filter the spaces that only apply to the building and store them in the state
      setSpacesInBuilding(
        allSpacesData.filter((space) => space.buildingUuid === selectedBuildingUuid)
      );
    }
  }, [selectedBuildingUuid, allSpacesData]);

  useEffect(() => {
    if (!!selectedBuildingUuid && !!spacesInBuilding.length) {
      // when the selected building changes, select its first space
      setSelectedSpaceUuid(spacesInBuilding[0].uuid ?? '');
    }
  }, [selectedBuildingUuid, spacesInBuilding]);

  /// useEffects to restore the last selections from localstorage ///

  useEffect(() => {
    const selectedBuildingData = buildingsData?.find(
      (building) => building.uuid === lsSelectedBuildingUuid
    );
    // If no building is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      selectedBuildingData
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
      setSelectedBuildingTimezone(selectedBuildingData?.timezone ?? 'UTC');
      setSelectedBuildingCapacity(selectedBuildingData?.capacity);
      if (!selectedBuildingData?.capacity) {
        setShowMaxCapacityAppless(false);
      }
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  useEffect(() => {
    // If no space is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!spacesInBuilding &&
      !!spacesInBuilding.length &&
      !selectedSpaceUuid &&
      lsSelectedSpaceUuid &&
      (spacesInBuilding.find((space) => space.uuid === lsSelectedSpaceUuid) ||
        ALL_SPACES === lsSelectedSpaceUuid)
    ) {
      // set the local storage value, if none exists default to the first space
      setSelectedSpaceUuid(lsSelectedSpaceUuid || ALL_SPACES);
    }
  }, [spacesInBuilding, lsSelectedSpaceUuid, selectedSpaceUuid]);

  /// Event handlers ///

  const handleStartDateChange = (date: Date | null) => {
    if (!!date && date <= endDate) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!!date && date >= startDate) {
      setEndDate(date);
    }
  };

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const buildingId = event.target.value as string;
    setSelectedBuildingUuid(buildingId);
    const selectedBuildingData = buildingsData?.find((building) => building.uuid === buildingId);
    setSelectedBuildingTimezone(selectedBuildingData?.timezone ?? 'UTC');
    setSelectedBuildingCapacity(selectedBuildingData?.capacity);
    if (!selectedBuildingData?.capacity) {
      setShowMaxCapacityAppless(false);
    }
    // store the last selected building in localstorage
    localStorage.setItem(LS_KEY_BUILDING, buildingId);
  };

  const handleSpaceChange = (event: SelectChangeEvent) => {
    const space = event.target.value as string;
    setSelectedSpaceUuid(space);
    // store the last selected space in localstorage
    localStorage.setItem(LS_KEY_SPACE, space);
  };

  const handleSensorTypeChange = (sensorType: string) => {
    setSpaceReportingType(sensorType as SpaceReportingType);
    localStorage.setItem(LS_KEY_SPACE_REPORTING_TAB, sensorType);
  };

  const handleAggregationChange = (event: SelectChangeEvent) => {
    const aggregation = event.target.value as Aggregation;
    setSelectedAggregation(aggregation);
  };

  const toggleShowMaxCapacity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMaxCapacity(event.target.checked);
  };

  const toggleShowMaxCapacityPrediction = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMaxCapacityPrediction(event.target.checked);
  };

  const toggleShowMaxCapacityAppless = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMaxCapacityAppless(event.target.checked);
  };

  const aggregatedUtilization = generateUtilizationRows(spacesInBuilding, spaceUsageData);
  const selectedSpaceUsage =
    selectedSpaceUuid === ALL_SPACES
      ? buildingUsageData
      : getSelectedSpaceGraphData(aggregatedUtilization, selectedSpaceUuid);
  const popularityRows = generatePopularityRows(spacesInBuilding, spaceUsageData);
  const selectedSpaceCapacity = getSpaceCapacity(
    allSpacesData,
    selectedSpaceUuid,
    selectedBuildingUuid,
    selectedBuildingCapacity
  );
  const peakRows = getPeakUsageForAllSpaces(spacesInBuilding, spaceUsageData);
  const occupancyData = getOccupancyGraphData(spaceOccupancyData, selectedBuildingTimezone);
  const forecastDataMain = getForecastGraphData(
    startDate,
    endDate,
    selectedForecastPeriod,
    predictionData,
    selectedBuildingTimezone
  );
  // appless occupancy (lidar) constants
  const applessOccupancySensors = applessOccupancySensorsData?.filter(
    (item) => item.buildingUuid === selectedBuildingUuid
  );
  const applessOccupancyData = calculateApplessOccupancyOverTime(
    applessOccupancyEventsData,
    new Date(startDate),
    new Date(endDate)
  );
  const applessAggregatedOccupancyData = aggregateApplessOccupancyData(
    applessOccupancyData.dates,
    applessOccupancyData.occupants,
    selectedAggregation,
    selectedBuildingTimezone
  );
  const buildingApplessOccupancyLogs = transformApplessOccupancyLogsData(
    applessOccupancyEventsData,
    applessOccupancySensorsData,
    undefined,
    startDate,
    endDate
  );

  const noSpaces =
    !isAllSpacesLoading && !isBuildingsLoading && (!spacesInBuilding || !spacesInBuilding.length);

  return (
    <div style={{ marginTop: 40 }}>
      <Grid container justifyContent="space-between" style={{ paddingBottom: 20 }}>
        <Grid item>
          <h1 style={{ paddingBottom: 20 }}>Spaces Reporting</h1>
        </Grid>
        <Grid item>
          <ExportSpaceCSV
            entityName={`Export CSV`}
            spacesInBuilding={spacesInBuilding}
            aggregatedUtilization={aggregatedUtilization}
            buildingsData={buildingsData}
            selectedBuildingUuid={selectedBuildingUuid}
            selectedAggregation={selectedAggregation}
            startDate={startDate}
            endDate={endDate}
            selectedSpaceReportingType={selectedSpaceReportingType}
            applessOccupancyLogs={buildingApplessOccupancyLogs}
            selectedBuildingTimezone={selectedBuildingTimezone}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1} style={{ paddingBottom: 20 }} justifyContent="space-between">
        <Grid item>
          <ToggleSwitch
            option1={SpaceReportingType.Occupants}
            option1Tooltip="'Occupants' tracks users with the b-line mobile app"
            option2={SpaceReportingType.People}
            option2Tooltip="'People' tracks anybody visiting, including occupants"
            selectedOption={selectedSpaceReportingType}
            onChange={handleSensorTypeChange}
          />
        </Grid>
        <Grid item sm style={{ maxWidth: 950 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} lg={3}>
              <FormControl fullWidth size="small" disabled={isBuildingsLoading}>
                <InputLabel>Building</InputLabel>
                <Select
                  value={selectedBuildingUuid}
                  sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                  disabled={!buildingsData}
                  onChange={handleBuildingChange}
                  label="Building"
                >
                  {!!buildingsData &&
                    buildingsData
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((building) => {
                        return (
                          <MenuItem key={building.uuid} value={building.uuid}>
                            {building.name}
                          </MenuItem>
                        );
                      })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <DateTimePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    InputProps: {
                      sx: { borderRadius: '10px', backgroundColor: 'white' }
                    }
                  }
                }}
                maxDateTime={endDate}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <DateTimePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    InputProps: {
                      sx: { borderRadius: '10px', backgroundColor: 'white' }
                    }
                  }
                }}
                minDateTime={startDate}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedAggregation}
                  onChange={handleAggregationChange}
                  sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                >
                  <MenuItem value={'hourly'}>Hourly</MenuItem>
                  <MenuItem value={'daily'}>Daily</MenuItem>
                  <MenuItem value={'monthly'}>Monthly</MenuItem>
                  <MenuItem value={'yearly'}>Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {selectedSpaceReportingType === SpaceReportingType.Occupants && (
        <>
          {noSpaces && (
            <SensorUpsellBanner
              sensorType="smart"
              hook="Do you want to know how your spaces are being used?"
            />
          )}

          <div style={containerStyle}>
            <Grid container justifyContent="space-between" sx={{ paddingLeft: 2, paddingRight: 2 }}>
              <Grid item xs={12} md={6}>
                <Grid container justifyContent="left">
                  <Grid item>
                    <h3 style={{ marginTop: 10, marginRight: 10 }}>
                      Space Utilization Distribution
                    </h3>
                  </Grid>
                  <Grid item>
                    <HelpTooltipIcon
                      message={`The average ${selectedAggregation} distribution of unique occupants who visited the space.`}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6} style={{ maxWidth: 333 }}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={isAllSpacesLoading || isBuildingsLoading}
                >
                  <Select
                    value={selectedSpaceUuid}
                    sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                    disabled={!spacesInBuilding || !spacesInBuilding.length}
                    onChange={handleSpaceChange}
                  >
                    <MenuItem key={ALL_SPACES} value={ALL_SPACES}>
                      {ALL_SPACES}
                    </MenuItem>
                    {!!spacesInBuilding &&
                      spacesInBuilding
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((space) => {
                          return (
                            <MenuItem key={space.uuid} value={space.uuid}>
                              {space.name}
                            </MenuItem>
                          );
                        })}
                  </Select>
                  {!!selectedSpaceCapacity && (
                    <FormHelperText>Capacity: {selectedSpaceCapacity}</FormHelperText>
                  )}
                  {noSpaces && (
                    <FormHelperText>No spaces available for selected building</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <TimeSeriesBarGraph
              x={selectedSpaceUsage?.x ?? []}
              y={selectedSpaceUsage?.y ?? []}
              loading={isBuildingsLoading || isSpaceUsageLoading || isBuildingUsageLoading}
              aggregation={selectedAggregation}
              noDataHint="Consider updating your building, space, and date filters"
              yAxisLabel="Avg. unique occupants"
            />

            <Grid container justifyContent="space-between" sx={{ padding: 2 }}>
              <Grid item>
                <Grid container justifyContent="left">
                  <Grid item>
                    <h3 style={{ marginTop: 10, marginRight: 10 }}>Space Occupancy</h3>
                  </Grid>
                  <Grid item>
                    <HelpTooltipIcon message={`The occupants in the space at the same time.`} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <SeafoamCheckbox checked={showMaxCapacity} onChange={toggleShowMaxCapacity} />
                  }
                  label="Show Max Capacity"
                />
              </Grid>
            </Grid>
            <OccupancyGraph
              dates={occupancyData.dates ?? []}
              occupants={occupancyData.occupants ?? []}
              capacity={selectedSpaceCapacity}
              loading={isBuildingsLoading || isSpaceOccupancyLoading}
              showMaxCapacity={showMaxCapacity}
            />
            <Grid container justifyContent="space-between" sx={{ padding: 2 }}>
              <Grid item xs={12} md={6}>
                <Grid container alignItems="center">
                  <Grid item>
                    <h3 style={{ marginTop: 10, marginRight: 10 }}>Occupancy Prediction</h3>
                  </Grid>
                  <Grid item>
                    <HelpTooltipIcon
                      message={`Forecasted number of occupants in the space at the same time.`}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Predict</InputLabel>
                  <Select
                    value={selectedForecastPeriod}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      padding: '4px 12px',
                      height: '40px',
                      '& .MuiSelect-select': {
                        padding: '4px 12px'
                      }
                    }}
                    onChange={handleFrequencyChange}
                    label="Predict"
                    displayEmpty
                    inputProps={{ 'aria-label': 'Select Frequency' }}
                  >
                    {Object.values(ForecastPeriod).map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>
                        {frequency}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedForecastPeriod === ForecastPeriod.Custom && (
                    <FormHelperText sx={{ fontSize: '0.775rem', lineHeight: '0.8rem' }}>
                      Select Start and End Dates above
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <SeafoamCheckbox
                      style={{ marginLeft: 30, padding: '5px' }}
                      checked={showMaxCapacityPrediction}
                      onChange={toggleShowMaxCapacityPrediction}
                    />
                  }
                  label="Show Max Capacity"
                />
              </Grid>
            </Grid>
            {/* Forecast graph */}
            <PredictionGraph
              pastData={forecastDataMain.past}
              futureData={forecastDataMain.future}
              capacity={selectedSpaceCapacity}
              loading={isBuildingsLoading || isPredictionLoading}
              showMaxCapacity={showMaxCapacityPrediction}
              forecastFrequency={selectedForecastPeriod}
            />
          </div>

          <Grid container wrap={isDesktop ? 'nowrap' : 'wrap'} gap={2} style={{ width: '100%' }}>
            <Grid item xs={12} md={7} style={containerStyle}>
              <Grid container justifyContent="left">
                <Grid item>
                  <h3 style={{ margin: 10 }}>Peak Space Utilization</h3>
                </Grid>
                <Grid item>
                  <HelpTooltipIcon message="The time at which spaces were the busiest." />
                </Grid>
              </Grid>
              <PeakSpaceTable
                peakRows={peakRows}
                loading={isBuildingsLoading || isSpaceUsageLoading}
                aggregation={selectedAggregation}
              />
            </Grid>
            <Grid item xs={12} md={5} style={containerStyle}>
              <Grid container justifyContent="left">
                <Grid item>
                  <h3 style={{ margin: 10 }}>Average Space Utilization</h3>
                </Grid>
                <Grid item>
                  <HelpTooltipIcon message="The average number of unique occupants who visited a space." />
                </Grid>
              </Grid>
              <SpacePopularityTable
                popularityRows={popularityRows}
                loading={isBuildingsLoading || isSpaceUsageLoading}
                aggregation={selectedAggregation}
              />
            </Grid>
          </Grid>
        </>
      )}

      {selectedSpaceReportingType === SpaceReportingType.People && (
        <>
          {!isBuildingsLoading &&
            !isApplessOccupancySensorsLoading &&
            !applessOccupancySensors?.length && (
              <SensorUpsellBanner
                sensorType="lidar"
                hook="Do you want to know how many people are visiting your building?"
              />
            )}
          <div style={containerStyle}>
            <Grid container justifyContent="left" sx={{ paddingLeft: 2, paddingRight: 2 }}>
              <Grid item>
                <h3 style={{ marginTop: 10, marginRight: 10 }}>Space Utilization Distribution</h3>
              </Grid>
              <Grid item>
                <HelpTooltipIcon
                  message={`The average ${selectedAggregation} distribution of people in the building.`}
                />
              </Grid>
            </Grid>
            <TimeSeriesBarGraph
              x={applessAggregatedOccupancyData.x}
              y={applessAggregatedOccupancyData.y}
              loading={isBuildingsLoading || isApplessOccupancyEventsLoading}
              aggregation={selectedAggregation}
              noDataHint="No occupancy data available"
              yAxisLabel="Average Occupancy"
            />

            <Grid container justifyContent="space-between" sx={{ paddingLeft: 2, paddingRight: 2 }}>
              <Grid item>
                <Grid container justifyContent="left">
                  <Grid item>
                    <h3 style={{ marginTop: 10, marginRight: 10 }}>Space Occupancy</h3>
                  </Grid>
                  <Grid item>
                    <HelpTooltipIcon message={`The people in the building at the same time.`} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <SeafoamCheckbox
                      checked={showMaxCapacityAppless}
                      onChange={toggleShowMaxCapacityAppless}
                      disabled={!selectedBuildingCapacity}
                    />
                  }
                  label="Show Max Capacity"
                />
              </Grid>
            </Grid>
            <OccupancyGraph
              dates={applessOccupancyData.dates ?? []}
              occupants={applessOccupancyData.occupants ?? []}
              capacity={selectedBuildingCapacity}
              loading={isBuildingsLoading || isApplessOccupancyEventsLoading}
              showMaxCapacity={showMaxCapacityAppless}
            />
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <div style={containerStyle}>
                <h3 style={{ margin: 10 }}>Lidar Sensor Logs</h3>
                <ApplessOccupancyLogsTable
                  rows={buildingApplessOccupancyLogs}
                  loading={isBuildingsLoading || isApplessOccupancyEventsLoading}
                  timeZone={selectedBuildingTimezone}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div style={containerStyle}>
                    <Grid container justifyContent="space-between" style={{ margin: 10 }}>
                      <Grid item xs={12} sm={6}>
                        <h3>Lidar Traffic</h3>
                      </Grid>
                      <Grid item xs={12} sm={6} style={{ textAlign: 'right', paddingRight: 20 }}>
                        {format(startDate, 'MMMM dd yyyy', { timeZone: selectedBuildingTimezone })}{' '}
                        - {format(endDate, 'MMMM dd yyyy', { timeZone: selectedBuildingTimezone })}
                      </Grid>
                      <div style={{ marginTop: 30 }}>
                        <span style={{ fontWeight: 'bold', fontSize: 32 }}>
                          {countApplessEvents(buildingApplessOccupancyLogs).entries}
                        </span>
                        <span style={{ marginLeft: 10 }}>Entries</span>
                        <span
                          style={{
                            color: colours.warmGrey,
                            fontWeight: 'bold',
                            fontSize: 32,
                            marginLeft: 50
                          }}
                        >
                          {countApplessEvents(buildingApplessOccupancyLogs).exits}
                        </span>
                        <span style={{ marginLeft: 10 }}>Exits</span>
                      </div>
                    </Grid>
                  </div>
                </Grid>
                {applessOccupancySensors?.map((sensor) => {
                  const sensorApplessOccupancyLogs = transformApplessOccupancyLogsData(
                    applessOccupancyEventsData,
                    applessOccupancySensorsData,
                    sensor.sensorId,
                    startDate,
                    endDate
                  );
                  const sensorLogCounts = countApplessEvents(sensorApplessOccupancyLogs);

                  return (
                    <Grid item xs={12} sm={6} key={sensor.uuid}>
                      <div style={containerStyle}>
                        <div style={{ margin: 10 }}>
                          <h4>{sensor.location}</h4>

                          <div style={{ marginTop: 30 }}>
                            <span style={{ fontWeight: 'bold', fontSize: 32 }}>
                              {sensorLogCounts.entries}
                            </span>
                            <span style={{ marginLeft: 20 }}>Entries</span>
                          </div>
                          <div>
                            <span
                              style={{ color: colours.warmGrey, fontWeight: 'bold', fontSize: 32 }}
                            >
                              {sensorLogCounts.exits}
                            </span>
                            <span style={{ marginLeft: 20 }}>Exits</span>
                          </div>
                        </div>
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}
